import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Tag, ArrowLeft, ExternalLink, 
  Star, Gift, TrendingUp, Filter, Bell, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from '@/components/ThemeToggle';
import { UserMenu } from '@/components/UserMenu';
import { ScrollReveal } from '@/components/ScrollReveal';
import { DealSubscriptionForm } from '@/components/DealSubscriptionForm';
import { useAffiliateTracking } from '@/hooks/useAffiliateTracking';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { VergoLogo } from '@/components/VergoLogo';
import { SEOHead } from '@/components/SEOHead';

interface Deal {
  id: string;
  title: string;
  description: string | null;
  original_price: number | null;
  discounted_price: number | null;
  discount_percentage: number | null;
  coupon_code: string | null;
  affiliate_url: string | null;
  expires_at: string | null;
  is_active: boolean | null;
  click_count: number | null;
  bot_id: string | null;
  bot_name?: string;
  bot_category?: string;
}

const Deals = () => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [filter, setFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const { trackClick } = useAffiliateTracking();

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const { data, error } = await supabase
        .from('deals')
        .select(`
          *,
          bots:bot_id (name, use_case)
        `)
        .eq('is_active', true)
        .order('discount_percentage', { ascending: false });

      if (error) throw error;

      const mappedDeals = (data || []).map((d: any) => ({
        ...d,
        bot_name: d.bots?.name || 'AI Tool',
        bot_category: d.bots?.use_case || 'General',
      }));

      setDeals(mappedDeals);
    } catch (error) {
      console.error('Error fetching deals:', error);
      toast.error('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  const handleDealClick = async (deal: Deal) => {
    if (deal.bot_id) {
      await trackClick(deal.bot_id, deal.affiliate_url || undefined);
    }
    
    // Update click count
    await supabase
      .from('deals')
      .update({ click_count: (deal.click_count || 0) + 1 })
      .eq('id', deal.id);
    
    if (deal.affiliate_url) {
      window.open(deal.affiliate_url, '_blank');
    }
  };

  const formatPrice = (price: number | null) => {
    if (price === null || price === 0) return 'Free';
    return `$${price.toFixed(2)}`;
  };

  const categories = ['all', ...new Set(deals.map(d => d.bot_category).filter(Boolean))];
  const filteredDeals = filter === 'all' ? deals : deals.filter(d => d.bot_category === filter);
  const featuredDeals = deals.filter(d => (d.discount_percentage || 0) >= 30);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="AI Tool Deals & Discounts — Save on Premium AI | Vergo"
        description="Active discounts, coupons, and exclusive offers on premium AI tools. Curated and updated regularly so you pay less for the AI you actually want."
        keywords="AI tool deals, AI discounts, ChatGPT coupon, Claude discount, AI software deals, AI tool coupons, premium AI offers"
      />
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-border/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <VergoLogo size="lg" />
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/">
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              </Link>
              <ThemeToggle />
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Hero */}
        <ScrollReveal>
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 mb-6"
            >
              <Gift className="w-5 h-5 text-green-500" />
              <span className="text-green-600 dark:text-green-400 font-semibold">Exclusive Deals</span>
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-4">
              Save Big on AI Tools
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Curated discounts and special offers on the best AI tools. 
              Updated regularly to help you save money.
            </p>
          </div>
        </ScrollReveal>

        {/* Subscribe to Deal Alerts */}
        <ScrollReveal delay={0.1}>
          <div className="mb-12 p-6 glass rounded-2xl">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Bell className="w-6 h-6 text-primary" />
                <div>
                  <h3 className="font-semibold text-foreground">Get Deal Alerts</h3>
                  <p className="text-sm text-muted-foreground">Be notified when new deals are available</p>
                </div>
              </div>
              <Button onClick={() => setShowSubscribe(!showSubscribe)} variant="outline">
                {showSubscribe ? 'Hide Form' : 'Subscribe'}
              </Button>
            </div>
            {showSubscribe && (
              <div className="mt-6">
                <DealSubscriptionForm />
              </div>
            )}
          </div>
        </ScrollReveal>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No deals available</h3>
            <p className="text-muted-foreground">Check back soon for new offers!</p>
          </div>
        ) : (
          <>
            {/* Featured Deals */}
            {featuredDeals.length > 0 && (
              <ScrollReveal delay={0.2}>
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-primary" />
                    Featured Deals
                  </h2>
                  <div className="grid md:grid-cols-3 gap-6">
                    {featuredDeals.map((deal, index) => (
                      <motion.div
                        key={deal.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="relative group cursor-pointer"
                        onClick={() => handleDealClick(deal)}
                      >
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-accent rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-300" />
                        <div className="relative bg-card rounded-2xl p-6 border border-border h-full">
                          <div className="flex items-start justify-between mb-4">
                            <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                              {deal.discount_percentage ? `${deal.discount_percentage}% OFF` : 'DEAL'}
                            </Badge>
                            {deal.coupon_code && (
                              <Badge variant="outline" className="text-xs">
                                {deal.coupon_code}
                              </Badge>
                            )}
                          </div>
                          <h3 className="text-xl font-bold text-foreground mb-2">{deal.title}</h3>
                          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{deal.description}</p>
                          <div className="flex items-center gap-3 mb-4">
                            <span className="text-muted-foreground line-through">{formatPrice(deal.original_price)}</span>
                            <span className="text-2xl font-bold text-green-500">{formatPrice(deal.discounted_price)}</span>
                          </div>
                          <Button className="w-full">
                            Get Deal <ExternalLink className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </section>
              </ScrollReveal>
            )}

            {/* Category Filter */}
            <ScrollReveal delay={0.3}>
              <div className="flex flex-wrap gap-2 mb-8">
                <div className="flex items-center gap-2 mr-4">
                  <Filter className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Filter:</span>
                </div>
                {categories.map((cat) => (
                  <Button
                    key={cat}
                    variant={filter === cat ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter(cat as string)}
                    className="capitalize"
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </ScrollReveal>

            {/* All Deals Grid */}
            <ScrollReveal delay={0.4}>
              <section>
                <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                  <Tag className="w-6 h-6 text-primary" />
                  All Deals ({filteredDeals.length})
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredDeals.map((deal, index) => (
                    <motion.div
                      key={deal.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-card rounded-xl p-5 border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-lg cursor-pointer"
                      onClick={() => handleDealClick(deal)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <Badge variant="secondary" className="text-xs">
                          {deal.bot_category || 'General'}
                        </Badge>
                        <Badge className="bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30">
                          {deal.discount_percentage ? `${deal.discount_percentage}% OFF` : 'DEAL'}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">{deal.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{deal.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground line-through">{formatPrice(deal.original_price)}</span>
                          <span className="text-lg font-bold text-green-500">{formatPrice(deal.discounted_price)}</span>
                        </div>
                        <Button size="sm" variant="outline">
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Visit
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            </ScrollReveal>
          </>
        )}

        {/* Affiliate Disclosure */}
        <ScrollReveal delay={0.5}>
          <div className="mt-12 p-6 bg-muted/50 rounded-xl text-center">
            <p className="text-sm text-muted-foreground">
              <strong>Disclosure:</strong> Some links on this page are affiliate links. 
              We may earn a commission if you make a purchase, at no extra cost to you. 
              This helps support Vergo and allows us to continue providing free AI tool recommendations.
            </p>
          </div>
        </ScrollReveal>
      </main>

      {/* Footer */}
      <footer className="bg-card/50 border-t border-border py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>© 2024 Vergo. Find your perfect AI companion at the best price.</p>
        </div>
      </footer>
    </div>
  );
};

export default Deals;
